import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Loader2, MessageSquareHeart, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  reportsApi,
  type CustomerVoiceReport,
  type CustomerVoiceSectionAvg,
} from "@/api/reports";
import { useAuth } from "@/contexts/AuthContext";
import { superAdminApi } from "@/api/superAdmin";
import { formatDate } from "@/utils/dateUtils";

function formatNps(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  const rounded = Math.round(n);
  return rounded > 0 ? `+${rounded}` : String(rounded);
}

function formatAvg(n: number | null | undefined, digits = 2): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toFixed(digits);
}

function SectionAveragesTable({
  sections,
  emptyLabel,
}: {
  sections: CustomerVoiceSectionAvg[];
  emptyLabel: string;
}) {
  if (!sections.length) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Section</TableHead>
          <TableHead className="text-right">Average</TableHead>
          <TableHead className="text-right">Responses</TableHead>
          <TableHead className="text-right">Scale</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sections.map((s) => (
          <TableRow key={s.key}>
            <TableCell className="font-medium">{s.label}</TableCell>
            <TableCell className="text-right tabular-nums">
              {formatAvg(s.average)}
            </TableCell>
            <TableCell className="text-right tabular-nums">{s.count}</TableCell>
            <TableCell className="text-right text-muted-foreground">
              {s.min != null && s.max != null ? `${s.min}–${s.max}` : "—"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function CustomerVoicePage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [selectedHospitalId, setSelectedHospitalId] = useState("");
  const [from, setFrom] = useState(
    format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), "yyyy-MM-dd")
  );
  const [to, setTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const reportParams = isSuperAdmin
    ? { hospitalId: selectedHospitalId || undefined }
    : undefined;

  const { data: hospitals } = useQuery({
    queryKey: ["super-admin-hospitals"],
    queryFn: () => superAdminApi.getHospitals(),
    enabled: isSuperAdmin,
  });

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["reports-customer-voice", from, to, reportParams?.hospitalId],
    queryFn: async () => {
      const res = await reportsApi.getCustomerVoice(from, to, reportParams);
      return res.data?.data as CustomerVoiceReport;
    },
    enabled: !isSuperAdmin || Boolean(selectedHospitalId),
  });

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <MessageSquareHeart className="h-6 w-6 text-teal-700" />
            Customer Voice
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            OPD WhatsApp feedback and IPD Google Form ratings — average score per section.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Date range</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          {isSuperAdmin && (
            <div className="space-y-1.5 min-w-[200px]">
              <Label>Hospital</Label>
              <Select value={selectedHospitalId} onValueChange={setSelectedHospitalId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select hospital" />
                </SelectTrigger>
                <SelectContent>
                  {(hospitals || []).map((h: { id: string; name: string }) => (
                      <SelectItem key={h.id} value={h.id}>
                        {h.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="cv-from">From</Label>
            <Input
              id="cv-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-[160px]"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cv-to">To</Label>
            <Input
              id="cv-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-[160px]"
            />
          </div>
          {(isLoading || isFetching) && (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mb-2" />
          )}
        </CardContent>
      </Card>

      {isSuperAdmin && !selectedHospitalId && (
        <p className="text-sm text-muted-foreground">Select a hospital to load Customer Voice.</p>
      )}

      {isError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4 text-sm text-red-800">
            {(error as Error)?.message || "Failed to load Customer Voice."}
            <button type="button" className="ml-2 underline" onClick={() => refetch()}>
              Retry
            </button>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          {data.notes?.length > 0 && (
            <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-0.5">
              {data.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          )}

          <Tabs defaultValue="opd">
            <TabsList>
              <TabsTrigger value="opd">
                OPD Feedback ({data.opd.responseCount})
              </TabsTrigger>
              <TabsTrigger value="ipd">
                IPD Feedback ({data.ipd.responseCount})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="opd" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Section averages (1–5)</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Source: WhatsApp OPD doctor feedback campaign
                  </p>
                </CardHeader>
                <CardContent>
                  <SectionAveragesTable
                    sections={data.opd.sections}
                    emptyLabel="No OPD feedback in this range."
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Recent comments</CardTitle>
                </CardHeader>
                <CardContent>
                  {data.opd.recentComments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No comments in this range.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Patient</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead className="text-right">Rating</TableHead>
                          <TableHead>Comment</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.opd.recentComments.map((c, i) => (
                          <TableRow key={`${c.phone}-${c.receivedAt}-${i}`}>
                            <TableCell className="whitespace-nowrap">
                              {c.receivedAt ? formatDate(c.receivedAt) : "—"}
                            </TableCell>
                            <TableCell>{c.patientName || "—"}</TableCell>
                            <TableCell>{c.phone}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {c.rating ?? "—"}
                            </TableCell>
                            <TableCell className="max-w-md">{c.comment || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="ipd" className="space-y-4 mt-4">
              <Card className="border-teal-200 bg-teal-50/40">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Star className="h-4 w-4 text-teal-800" />
                    Net Promoter Score (NPS)
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    From “Likelihood to recommend us to others” (0–10): Promoters 9–10, Passives 7–8
                    (excluded), Detractors 0–6. NPS = % Promoters − % Detractors.
                  </p>
                </CardHeader>
                <CardContent className="flex flex-wrap items-baseline gap-6">
                  <div>
                    <div className="text-3xl font-semibold tabular-nums text-teal-900">
                      {formatNps(data.ipd.npsAverage)}
                    </div>
                    <div className="text-xs text-muted-foreground">Score (−100 to +100)</div>
                  </div>
                  <div>
                    <div className="text-lg font-medium tabular-nums">{data.ipd.npsCount}</div>
                    <div className="text-xs text-muted-foreground">Responses with NPS</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Section averages (1–5)</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Source: IPD Google Form sheet
                    {data.ipd.sheetId ? ` (${data.ipd.sheetId.slice(0, 8)}…)` : ""}
                  </p>
                </CardHeader>
                <CardContent>
                  <SectionAveragesTable
                    sections={data.ipd.sections}
                    emptyLabel="No IPD feedback in this range (or sheet not readable)."
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Recent comments</CardTitle>
                </CardHeader>
                <CardContent>
                  {data.ipd.recentComments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No comments in this range.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Patient</TableHead>
                          <TableHead>ID</TableHead>
                          <TableHead className="text-right">Overall</TableHead>
                          <TableHead className="text-right">NPS</TableHead>
                          <TableHead>Standout staff</TableHead>
                          <TableHead>Comment</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.ipd.recentComments.map((c, i) => (
                          <TableRow key={`${c.patientId}-${c.receivedAt}-${i}`}>
                            <TableCell className="whitespace-nowrap">
                              {c.receivedAt ? formatDate(c.receivedAt) : "—"}
                            </TableCell>
                            <TableCell>{c.patientName || "—"}</TableCell>
                            <TableCell>{c.patientId || "—"}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {c.overall ?? "—"}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {c.nps ?? "—"}
                            </TableCell>
                            <TableCell className="max-w-[140px]">
                              {c.standoutStaff || "—"}
                            </TableCell>
                            <TableCell className="max-w-md">{c.comment || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
